/**
 * The period control the report screens share.
 *
 * One place for the day arithmetic and the Daily/Weekly/Monthly + date-picker
 * bar, so every report frames the same stretch of days the same way and reads
 * the same dates back. The dashboard keeps its own copy (it shipped first); new
 * reports take theirs from here.
 */
import { useMemo, useState } from 'react';

import { useTradingDay } from '../pos/useBusinessDay.js';
import { RangeInputs } from './DashboardPage.jsx';

export function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function parseYmd(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
export function shiftDays(s, n) {
  const d = parseYmd(s);
  d.setDate(d.getDate() + n);
  return ymd(d);
}
export const TODAY = ymd(new Date());

/** The [from, to] a mode and an anchor day resolve to. Never past the latest trading day. */
export function rangeFor(mode, anchor, latest = TODAY, custom = null) {
  if (mode === 'day') return [anchor, anchor];
  // Any two days, chosen by hand; never past the latest trading day.
  if (mode === 'range' && custom) return [custom.from, custom.to > latest ? latest : custom.to];
  const d = parseYmd(anchor);
  if (mode === 'week') {
    const dow = (d.getDay() + 6) % 7; // 0 = Monday
    const from = shiftDays(anchor, -dow);
    const to = shiftDays(from, 6);
    return [from, to > latest ? latest : to];
  }
  const from = ymd(new Date(d.getFullYear(), d.getMonth(), 1));
  const to = ymd(new Date(d.getFullYear(), d.getMonth() + 1, 0));
  return [from, to > latest ? latest : to];
}

export function prettyDay(s) {
  return parseYmd(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
export function periodLabel(mode, from, to) {
  if (mode === 'day' || from === to) return prettyDay(from);
  if (mode === 'month') {
    return parseYmd(from).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
  const a = parseYmd(from).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const b = parseYmd(to).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `${a} – ${b}`;
}

/** One entry per calendar day in the range, zeros filled in — see the dashboard. */
export function fillDays(from, to, series, keys = ['orders', 'gross', 'net']) {
  const byDate = Object.fromEntries((series ?? []).map((r) => [r.date, r]));
  const out = [];
  let d = from;
  for (let guard = 0; d <= to && guard < 400; guard++) {
    const r = byDate[d] ?? {};
    const row = { date: d };
    for (const k of keys) row[k] = Number(r[k]) || 0;
    out.push(row);
    d = shiftDays(d, 1);
  }
  return out;
}

/** Period state, ready to spread into a query and a <PeriodBar>. */
export function usePeriod(initialMode = 'day') {
  const [mode, setMode] = useState(initialMode);
  // Anchored on the trading day the shop has open, not the calendar (see useTradingDay).
  const tradingDay = useTradingDay();
  const latest = tradingDay && tradingDay > TODAY ? tradingDay : TODAY;
  const [picked, setAnchor] = useState(null);
  const anchor = picked ?? tradingDay ?? TODAY;
  const [custom, setCustom] = useState(null);
  const [from, to] = useMemo(() => rangeFor(mode, anchor, latest, custom ?? { from: anchor, to: anchor }), [mode, anchor, latest, custom]);
  return {
    mode,
    anchor,
    from,
    to,
    latest,
    range: { from, to },
    setRange: setCustom,
    label: periodLabel(mode, from, to),
    includesToday: to >= (tradingDay ?? TODAY),
    setMode,
    setAnchor,
    periodWord: mode === 'day' ? 'day' : mode === 'week' ? 'week' : mode === 'month' ? 'month' : 'period',
  };
}

export function PeriodBar({ mode, anchor, setMode, setAnchor, label, latest = TODAY, range, setRange }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="inline-flex rounded-lg border border-rule bg-surface-3 p-0.5">
        {[
          ['day', 'Daily'],
          ['week', 'Weekly'],
          ['month', 'Monthly'],
          ['range', 'Range'],
        ].map(([m, text]) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            aria-pressed={mode === m}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              mode === m ? 'bg-brand-500 text-black' : 'text-ink-2 hover:text-ink'
            }`}
          >
            {text}
          </button>
        ))}
      </div>

      {mode === 'range' ? (
        <RangeInputs range={range} latest={latest} onRange={setRange} />
      ) : (
      <label className="flex items-center gap-2 text-sm text-ink-2">
          <span className="text-ink-3">On</span>
          <input
            type="date"
            value={anchor}
            max={latest}
            onChange={(e) => e.target.value && setAnchor(e.target.value)}
            aria-label="Pick a day"
            className="h-9 rounded-lg border border-rule bg-surface-3 px-2.5 text-sm text-ink focus:border-brand-400 focus:outline-none"
          />
        </label>
        )}

      <span className="ml-auto text-sm text-ink-3">{label}</span>
    </div>
  );
}
